import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth';
import { CalendarButtons } from './CalendarButtons';
import { NavBar } from '../../components/NavBar';
import { SharedFooter } from '../../components/SharedFooter';

interface InvitationData {
  name: string;
  description: string | null;
  code: string;
  scheduledAt: string | null;
  endsAt: string | null;
  timezone: string | null;
  hasStarted: boolean;
  isClosed: boolean;
  hostName: string | null;
  imageUrl: string | null;
}

const SESSION_OPEN_POLL_INTERVAL_MS = 15_000;
const SESSION_OPEN_POLL_BEFORE_MS = 45 * 60 * 1000;
const SESSION_OPEN_POLL_AFTER_MS = 15 * 60 * 1000;
const SESSION_OPEN_WAKE_CHECK_INTERVAL_MS = 60 * 60 * 1000;
const DEFAULT_SESSION_DURATION_MS = 2 * 60 * 60 * 1000;

interface CancelReservationResponse {
  success?: boolean;
  cancellationType?: 'free' | 'paid';
  refundRequest?: {
    id: string;
    status: 'pending' | 'handled';
  } | null;
  error?: string;
}

function getSessionOpenPollingDelay(invitation: InvitationData) {
  if (!invitation.scheduledAt) return null;

  const startTime = new Date(invitation.scheduledAt).getTime();
  if (!Number.isFinite(startTime)) return null;

  const endTime = invitation.endsAt ? new Date(invitation.endsAt).getTime() : startTime + DEFAULT_SESSION_DURATION_MS;
  const resolvedEndTime = Number.isFinite(endTime) && endTime > startTime
    ? endTime
    : startTime + DEFAULT_SESSION_DURATION_MS;
  const pollStartTime = startTime - SESSION_OPEN_POLL_BEFORE_MS;
  const pollEndTime = resolvedEndTime + SESSION_OPEN_POLL_AFTER_MS;
  const now = Date.now();

  if (now > pollEndTime) return null;
  return Math.max(0, pollStartTime - now);
}

export function ReservationConfirmedPage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getToken } = useAuth();
  const [invitation, setInvitation] = useState<InvitationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [canceling, setCanceling] = useState(false);
  const checkoutConfirmationRef = useRef<string | null>(null);

  const fetchInvitation = useCallback(async (options: { confirmCheckout?: boolean } = {}) => {
    try {
      const token = await getToken();
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const checkoutSessionId = searchParams.get('session_id');
      if (
        options.confirmCheckout &&
        searchParams.get('payment') === 'success' &&
        checkoutSessionId &&
        checkoutConfirmationRef.current !== checkoutSessionId &&
        token
      ) {
        await fetch('/api/stripe/checkout/confirm-session', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ sessionId: checkoutSessionId }),
        });
        checkoutConfirmationRef.current = checkoutSessionId;
      }

      const response = await fetch(`/api/rooms/${code}/invitation`, { headers });
      if (!response.ok) {
        navigate(`/s/${code}`);
        return;
      }
      const data = await response.json();
      setInvitation(data);
    } catch (err) {
      navigate(`/s/${code}`);
    } finally {
      setLoading(false);
    }
  }, [code, getToken, navigate, searchParams]);

  useEffect(() => {
    if (code) {
      fetchInvitation({ confirmCheckout: true });
    }
  }, [code, fetchInvitation]);

  useEffect(() => {
    if (!code || !invitation || invitation.hasStarted || invitation.isClosed) {
      return;
    }

    const pollingDelay = getSessionOpenPollingDelay(invitation);
    if (pollingDelay === null) return;

    if (pollingDelay > 0) {
      const timeout = window.setTimeout(() => {
        fetchInvitation();
      }, Math.min(pollingDelay, SESSION_OPEN_WAKE_CHECK_INTERVAL_MS));

      return () => window.clearTimeout(timeout);
    }

    const interval = window.setInterval(() => {
      fetchInvitation();
    }, SESSION_OPEN_POLL_INTERVAL_MS);

    return () => window.clearInterval(interval);
  }, [code, fetchInvitation, invitation]);

  const handleCancelReservation = async () => {
    if (!code) return;
    if (invitation?.hasStarted || invitation?.isClosed) return;

    const confirmed = window.confirm('Are you sure you want to cancel your reservation?');
    if (!confirmed) return;

    setCanceling(true);
    try {
      const token = await getToken();
      const response = await fetch(`/api/rooms/${code}/rsvp`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json().catch(() => ({} as CancelReservationResponse)) as CancelReservationResponse;
      if (response.ok) {
        if (data.cancellationType === 'paid' && data.refundRequest?.status === 'pending') {
          navigate(`/s/${code}?refund=requested`);
          return;
        }
        navigate(`/s/${code}`);
      } else if (data.error) {
        window.alert(data.error);
      }
    } catch (err) {
      console.error('Error canceling reservation:', err);
    } finally {
      setCanceling(false);
    }
  };

  const formatEventDate = (dateString: string | null) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return {
      month: date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
      day: date.toLocaleDateString('en-US', { day: '2-digit' }),
      weekday: date.toLocaleDateString('en-US', { weekday: 'long' }),
      date: date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' }),
      time: date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-line border-t-accent rounded-full animate-spin" />
      </div>
    );
  }

  if (!invitation) {
    return null;
  }

  const eventDate = formatEventDate(invitation.scheduledAt);
  const isLive = invitation.hasStarted && !invitation.isClosed;

  return (
    <div className="min-h-screen bg-bg relative z-10 pt-[72px]">
      <NavBar variant="light" />

      {/* Main Content */}
      <main className="py-14 pb-32">
        <div className="max-w-[600px] mx-auto px-8">
          <div className="bg-panel border border-line rounded-[20px] shadow-xl overflow-hidden">
            {/* Success Header */}
            <div className="bg-ok/10 border-b border-ok/20 px-8 py-6 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-ok/20 mb-4">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-ok">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <h1 className="font-display text-3xl font-medium text-text">Reserved</h1>
            </div>

            {/* Event Details */}
            <div className="p-8">
              {/* Event Image */}
              {invitation.imageUrl && (
                <div className="aspect-[2/1] rounded-xl overflow-hidden mb-6 border border-line">
                  <img
                    src={invitation.imageUrl}
                    alt={invitation.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              {/* Event Name */}
              <h2 className="font-display text-2xl font-medium text-text mb-2">
                {invitation.name}
              </h2>

              {/* Host */}
              {invitation.hostName && (
                <p className="text-text-dim text-sm mb-4">
                  Hosted by <span className="text-text font-medium">{invitation.hostName}</span>
                </p>
              )}

              {/* Date/Time */}
              {eventDate && (
                <div className="flex flex-col gap-4 bg-panel2 rounded-xl border border-line p-4 mb-6 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex min-w-0 items-center gap-4">
                    <div className="w-[64px] flex-shrink-0 overflow-hidden rounded-lg border border-line bg-panel text-center shadow-sm">
                      <div className="bg-text px-2 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-white">
                        {eventDate.month}
                      </div>
                      <div className="py-2 font-display text-3xl leading-none text-text">
                        {eventDate.day}
                      </div>
                    </div>
                    <div className="min-w-0">
                      <div className="font-display text-lg text-text">{eventDate.weekday}</div>
                      <div className="text-sm text-text-dim">
                        {eventDate.date} · {eventDate.time}
                      </div>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <CalendarButtons
                      eventName={invitation.name}
                      scheduledAt={invitation.scheduledAt}
                      endsAt={invitation.endsAt}
                      timezone={invitation.timezone}
                      roomCode={code || ''}
                    />
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col gap-3 pt-4 border-t border-line">
                {isLive ? (
                  <Link
                    to={`/s/${code}/join`}
                    className="btn btn-accent btn-lg w-full justify-center"
                  >
                    Enter Session
                  </Link>
                ) : (
                  <Link
                    to={`/s/${code}`}
                    className="btn btn-lg w-full justify-center"
                  >
                    Back to event details
                  </Link>
                )}
                {!invitation.hasStarted && !invitation.isClosed && (
                  <button
                    onClick={handleCancelReservation}
                    disabled={canceling}
                    className="text-sm text-text-dim hover:text-warn transition-colors"
                  >
                    {canceling ? 'Canceling...' : 'Cancel reservation'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      <SharedFooter />
    </div>
  );
}
